import type { PeerSearchPlace, PlaceSnapshot } from '@/types/gmb';

// UI-2B｜同業樣本相對位置計算（純顯示層）。
// 只使用「本次健診目標 + 本次有效同業樣本」，不呼叫 Google、不重算 Peer 演算法、
// 不重算中位數、不影響任何 Backend 分數 / Priority / Grade。
//
// Tie rule（同值處理）：採「標準競爭排名（standard competition ranking）」，
// 例如 1, 2, 2, 4。排名 = 1 + 嚴格優於我的樣本數；同值並列同名次，其後名次跳號。
// 輸入排序不影響結果（deterministic）。

export interface SamplePosition {
  /** 我的名次（競爭排名） */
  rank: number;
  /** 本次參與比較的總數（含自己） */
  total: number;
  /** 與我同值的其他樣本數 */
  tiedCount: number;
  /** 是否有同值並列 */
  isTied: boolean;
}

/**
 * 計算我在本次樣本中的相對位置。
 * - higherIsBetter = true 時，數值越高名次越前（評分、評論數）。
 * - target 缺失 或 沒有任何有效同業值 → 回傳 null（呼叫端顯示「資料不足」）。
 * - 缺失值（null / undefined / NaN）一律排除，不視為 0。
 */
export function computeSamplePosition(
  targetValue: number | null | undefined,
  peerValues: Array<number | null | undefined>,
  higherIsBetter = true,
): SamplePosition | null {
  if (typeof targetValue !== 'number' || Number.isNaN(targetValue)) return null;

  const valid = (peerValues || []).filter(
    (v): v is number => typeof v === 'number' && !Number.isNaN(v),
  );
  if (valid.length === 0) return null;

  const better = valid.filter((v) => (higherIsBetter ? v > targetValue : v < targetValue)).length;
  const tied = valid.filter((v) => v === targetValue).length;

  return {
    rank: better + 1,
    total: valid.length + 1,
    tiedCount: tied,
    isTied: tied > 0,
  };
}

/** 取出所有同業的評分（未提供者為 null，會被排名函式排除）。 */
export function collectPeerRatings(peers?: PeerSearchPlace[] | null): Array<number | null> {
  return (peers || []).map((p) => (typeof p.rating === 'number' ? p.rating : null));
}

/** 取出所有同業的評論數（未提供者為 null，會被排名函式排除）。 */
export function collectPeerReviewCounts(peers?: PeerSearchPlace[] | null): Array<number | null> {
  return (peers || []).map((p) => (typeof p.userRatingCount === 'number' ? p.userRatingCount : null));
}

/** 本次健診目標的評分（未提供 → null）。 */
export function targetRating(target: PlaceSnapshot): number | null {
  return typeof target.rating === 'number' ? target.rating : null;
}

/** 本次健診目標的評論數（未提供 → null）。 */
export function targetReviewCount(target: PlaceSnapshot): number | null {
  return typeof target.userRatingCount === 'number' ? target.userRatingCount : null;
}